import {
  assign,
  createActor,
  emit,
  enqueueActions,
  fromCallback,
  fromPromise,
  setup,
  waitFor,
} from 'xstate';

import { EluveError } from '@eluve/eluve-errors';
import {
  createDefaultLogActionFromCorrelatedLogger,
  logAction,
} from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';
import { getMetadataFromFileName } from '@eluve/utils';

import {
  CallbackActorInvoke,
  SupportedAudioFormat,
  UploadFileEvents,
  UploadType,
} from './types';

/**
 * The name of a DOMException error thrown when a file is not found
 */
export const NOT_FOUND_ERROR_NAME = 'NotFoundError';

export type UploadFileMachineInput = {
  fileName: string;
  tenantId: string;
  userId: string;
  appointmentId: string;
  uploadType: UploadType;
  format: SupportedAudioFormat;
  shouldBeTranscribed: boolean;
  wasDegraded: boolean;
  recordingStartedAt: string;
};

type GetFileDataArgs = Pick<
  UploadFileMachineInput,
  'fileName' | 'appointmentId' | 'tenantId'
>;

export type UploadFileMachineContext = UploadFileMachineInput & {
  file?: File;
  error?: Error | EluveError;
};

export type UploadFileArgs = {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  file: File;
  uploadType: UploadType;
  format: SupportedAudioFormat;
  shouldBeTranscribed: boolean;
  wasDegraded: boolean;
  recordingStartedAt: string;
  chunkNum?: number;
};

export type GetFileDataFn = (args: GetFileDataArgs) => Promise<File>;
export type CleanupFileFn = (args: {
  appointmentId: string;
  fileId: string;
}) => Promise<void>;

const getFileData = fromPromise<
  Awaited<ReturnType<GetFileDataFn>>,
  GetFileDataArgs
>(async () => {
  throw new Error('getFileData not implemented');
});

const cleanupFile = fromPromise<
  Awaited<ReturnType<CleanupFileFn>>,
  { fileId: string; appointmentId: string }
>(async () => {
  throw new Error('cleanupFile not implemented');
});

const uploadFile = fromCallback<UploadFileEvents, UploadFileArgs>(() => {
  throw new Error('uploadFile not implemented');
});

export const uploadFileMachine = setup({
  types: {
    context: {} as UploadFileMachineContext,
    input: {} as UploadFileMachineInput,
    events: {} as UploadFileEvents,
    emitted: {} as UploadFileEvents,
  },
  actors: {
    getFileData,
    uploadFile,
    cleanupFile,
  },
  guards: {
    isRetryableError: ({ event }) => {
      if ('error' in event && event.error) {
        if (
          event.error instanceof DOMException &&
          event.error.name === NOT_FOUND_ERROR_NAME
        ) {
          return false;
        }
      }
      return true;
    },
    hasFile: ({ context }) => Boolean(context?.file),
  },
  actions: {
    log: logAction,
    onError: enqueueActions(({ enqueue, event }) => {
      if ('error' in event && event.error) {
        enqueue({
          type: 'log',
          params: {
            level: 'warn',
            message: 'UploadFileMachine error',
            logMeta: {
              error: event.error ? event.error.message : 'Unknown error',
            },
          },
        });

        enqueue.assign({
          error: event.error,
        });
      }
    }),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFcAOAbA9gQwgMQEt0wBZbAYwAsCA7MAOgHEwAXQ4gEWxewGIJMderQBumANYM0WXO1IVqQ5myJguPBKMzluBQQG0ADAF0jxxKFSZYBFnpoWQAD0QBGABwBmegBYAnJ7uAOwATD6eQQBshpFBADQgAJ5uYfRBniHuIaEArD6Rnq7ZAL7FCdI4+KpkVLQMynLqfGAATi2YLfQY3ABmHQC29BWy1Qp1TKyN3NiaNGI6dgYmZo5WNosOSM6IALSRIfQ5hp5+4fleIa6RkTkJyQgnfvTuBZmRfjnuruk+peUYlTkNUU9UmqiavFa7U63RYfRag2GVWIwPGDXB01m810S1MrnMWzWtnsjhcCC+3mihkM6XcOQKxz8d0QZ3ork8HJy9Mi-kiXhyfxASKBYyEAGFKGByOI5LwVoTrMTBKSWYU0uzPDlPIYfDTTvEkogQtd6LFrlE8idrp5BcLRrVxZLpbL9PjVoqNiqEEd3Oqgq4jldtTEfMyHn5DIcisafEE-KEihFbQCRijRQwAKop5FgXgZgAKABkAPIAQQ4AH08ABJQsAUXoBZL5erADlGPLLB6SVsye5dWyeSEdXkbiEQp4w+F6IEcpkgkEtYY-H5gr8ykLsyKHZmt6o80Wy5Wa-X6HhS7W6xxOyAiZ7e4hrc8Oa4-JlXD5h4Uw8acr5Ndq7z0lyOR+MmMg5qiQhZhBspNkeVaXmel4VvB5ZXjed49qAZJvq4bI+D4oHXF8y6ToaCDDvh+QBhyNwBD4-bgYC9ogvQYrENgNBoLKAhCFokhDHuaY7uxnHcagchYtoOI0GYmHdsqD4IH4rj4S8YRZP6hSeIxYb7PhhhXOkRzpOkJQbnaIlsRxYBcTx+5Qh0XToL0AxCbBrHjLZ9mSao0kLPY8kmO66zYdsCBqZ+9DGmEOrvMOXL6SEf6GEchhhBkJEcqUG40JgEBwI4VnyDuoVKpsOG7GpkZHCcZx8hkxlhjs06qbGGonIU-jMampVseinDTOV95VQgOzaocpmfqu2peDcYb9s8fg3JqmTLguAqWcJ-XeU6MqqCN4VkrqPjPPkunuFkwTarGYYfn+NxBP27inHkK7sr1kHpo2O1HUpY0FI9hEnEZNxaoE93jjFOTPa+XKBBcW3-J51neeJDnEP9lURVFZ2xougQfqujGhhRJxBGyX6nEGH66Ta22o7tQgcIIYDY16E21dNISzYY823BR0T4ZEVzXDy9LuPzkS5cUQA */
  id: 'uploadFileMachine',
  initial: 'GetFileData',
  context: ({ input }) => input,
  states: {
    GetFileData: {
      invoke: {
        src: 'getFileData',
        input: ({ context }) => ({
          appointmentId: context.appointmentId,
          fileName: context.fileName,
          tenantId: context.tenantId,
        }),
        onDone: {
          actions: assign({
            file: (context) => context.event.output,
          }),
          target: 'CheckFile',
        },
        onError: [
          {
            target: 'Done',
            actions: ['onError'],
            guard: 'isRetryableError',
          },
          {
            target: 'CleanupFile',
            actions: ['onError'],
          },
        ],
      },
    },
    CheckFile: {
      always: [
        {
          target: 'UploadFile',
          guard: 'hasFile',
        },
        {
          target: 'Done',
        },
      ],
    },
    UploadFile: {
      invoke: {
        src: 'uploadFile',
        input: ({ context }) => {
          const { file, fileName, userId, ...rest } = context;
          const { databaseFileId } = getMetadataFromFileName({
            fileName,
            userId,
          });

          return {
            // This was verified in the CheckFile state
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            file: file!,
            fileId: databaseFileId,
            ...rest,
          };
        },
      },
      on: {
        'UPLOAD_FILE.UPLOADING': {
          actions: [emit(({ event }) => event)],
        },
        'UPLOAD_FILE.FAILED': {
          target: 'Done',
          actions: ['onError', emit(({ event }) => event)],
        },
        'UPLOAD_FILE.FILE_UPLOADED': {
          target: 'CleanupFile',
          actions: [emit(({ event }) => event)],
        },
      },
    },
    CleanupFile: {
      invoke: {
        src: 'cleanupFile',
        input: ({ context }) => ({
          appointmentId: context.appointmentId,
          fileId: context.fileName,
        }),
        onDone: 'Done',
        onError: { target: 'Done', actions: ['onError'] },
      },
    },
    Done: {
      type: 'final',
      entry: [
        emit(() => ({
          type: 'UPLOAD_FILE.DONE',
        })),
      ],
    },
  },
});

export type UploadFileMachineOptions = {
  getFile: GetFileDataFn;
  uploadFile: CallbackActorInvoke<UploadFileArgs, UploadFileEvents>;
  cleanupFile: CleanupFileFn;
  logger: CorrelatedLogger;
};

export const createUploadFileMachineLogic = (
  args: UploadFileMachineOptions,
) => {
  const { getFile, uploadFile, cleanupFile, logger } = args;
  return uploadFileMachine.provide({
    actions: {
      log: createDefaultLogActionFromCorrelatedLogger(logger),
    },
    actors: {
      getFileData: fromPromise(({ input }) => getFile(input)),
      uploadFile: fromCallback(({ input, sendBack, emit }) =>
        uploadFile({
          input,
          sendBack: (evt) => {
            sendBack(evt);
          },
          emit,
        }),
      ),
      cleanupFile: fromPromise(
        async ({ input: { fileId, appointmentId } }) =>
          await cleanupFile({ fileId, appointmentId }),
      ),
    },
  });
};

export const convertUploadFileMachineToCallback = (
  args: UploadFileMachineOptions,
) => {
  const wrapper: CallbackActorInvoke<
    UploadFileMachineInput,
    UploadFileEvents
  > = ({ input, sendBack: parentSendback, emit }) => {
    const actor = createActor(createUploadFileMachineLogic(args), {
      input,
    });

    const onEvent = actor.on('*', (e) => {
      parentSendback(e);
      emit(e);
    });

    (async () => {
      try {
        actor.start();

        await waitFor(actor, (state) => state.status === 'done');
      } catch (e) {
        parentSendback({ type: 'UPLOAD_FILE.FAILED', error: e as Error });
      }
    })();

    return () => {
      onEvent.unsubscribe();
    };
  };

  return wrapper;
};
